import React, { useState, useMemo } from 'react';
import ProjectDetailModal from './ProjectDetailModal';
import ExportButtons from './ExportButtons';

function statusBadge(status) {
  const map = { Active: 'badge-active', Completed: 'badge-completed', Pending: 'badge-pending', Rejected: 'badge-rejected', Disputed: 'badge-disputed' };
  return <span className={`badge ${map[status] || ''}`}>{status}</span>;
}

const fmt = n => n != null ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';
const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

function sortKey(p) {
  if (p.order_date) return p.order_date;
  const match = p.project_code?.match(/(\d{2})(\d+)$/);
  if (match) return `20${match[1]}-01-01`;
  return '1970-01-01';
}

function getPending(fin) {
  if (!fin) return 0;
  if (fin.pending_amount > 0) return fin.pending_amount;
  return Math.max(0, (fin.agreed_fee || 0) - (fin.paid_amount || 0));
}

const SERVICE_LABELS = {
  CMP: 'Construction Management Plan',
  CEMP: 'Construction Environmental Management Plan',
  CDM: 'Construction Design & Management',
  CLP: 'Construction Logistics Plan',
  CMS: 'Construction Method Statement',
  CTMP: 'Construction Traffic Management Plan',
  CLMP: 'Construction Logistics & Management Plan',
  DCMP: 'Demolition & Construction Management Plan',
  DCMLP: 'Demolition, Construction & Management Logistics Plan',
  SPA: 'Site Preliminary Assessment',
  EC: 'Ecological Constraints',
};

export default function ServicesPage({ projects, customers, user }) {
  const [selected, setSelected] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const activeProjects = projects.filter(p => p.status !== 'Rejected');

  const serviceStats = useMemo(() => {
    const map = {};
    activeProjects.forEach(p => {
      const svc = p.service_type || 'Other';
      if (!map[svc]) map[svc] = { projects: [], agreed: 0, paid: 0, pending: 0 };
      map[svc].projects.push(p);
      const fin = p.financials?.[0];
      map[svc].agreed += fin?.agreed_fee || 0;
      map[svc].paid += fin?.paid_amount || 0;
      map[svc].pending += getPending(fin);
    });
    return Object.entries(map)
      .map(([svc, d]) => ({ svc, ...d, count: d.projects.length }))
      .sort((a, b) => b.count - a.count);
  }, [activeProjects]);

  const selectedProjects = useMemo(() => {
    if (!selected) return [];
    return activeProjects
      .filter(p => p.service_type === selected)
      .sort((a, b) => sortKey(b) > sortKey(a) ? 1 : -1);
  }, [selected, activeProjects]);

  const custName = (id) => { const c = customers.find(c => c.id === id); return c ? c.full_name : '—'; };

  const totalProjects = activeProjects.length;

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Services</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>{serviceStats.length} service types · {totalProjects} projects</p>
        </div>
        <ExportButtons
          rows={serviceStats}
          columns={[
            { label: 'Service', get: s => s.svc },
            { label: 'Description', get: s => SERVICE_LABELS[s.svc] || s.svc },
            { label: 'Projects', get: s => s.count },
            { label: 'Total Agreed', get: s => s.agreed },
            { label: 'Avg Fee', get: s => s.count > 0 ? Math.round(s.agreed / s.count) : 0 },
            { label: 'Total Paid', get: s => s.paid },
            { label: 'Outstanding', get: s => s.pending },
          ]}
          title="Services Summary"
          filename="liongate_services"
        />
      </div>

      {/* Service cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginBottom: 28 }}>
        {serviceStats.map(({ svc, count, agreed, paid, pending, projects: svcProjects }) => {
          const isSelected = selected === svc;
          const collection = pct(paid, agreed);
          const avg = count > 0 ? agreed / count : 0;
          const active = svcProjects.filter(p => p.status === 'Active').length;
          const completed = svcProjects.filter(p => p.status === 'Completed').length;
          const disputed = svcProjects.filter(p => p.status === 'Disputed').length;

          return (
            <div
              key={svc}
              onClick={() => setSelected(isSelected ? null : svc)}
              style={{
                background: 'var(--bg2)',
                border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius2)',
                padding: '18px 20px',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--border2)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: 'var(--font)', fontWeight: 700, fontSize: 18, color: isSelected ? 'var(--accent)' : 'var(--text)', marginBottom: 3 }}>{svc}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', maxWidth: 180 }}>{SERVICE_LABELS[svc] || svc}</div>
                </div>
                <div style={{ fontFamily: 'var(--font)', fontWeight: 800, fontSize: 26, color: isSelected ? 'var(--accent)' : 'var(--text2)' }}>{count}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>AGREED</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{fmt(agreed)}</div>
                </div>
                <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>AVG FEE</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{fmt(avg)}</div>
                </div>
                <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>OUTSTANDING</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: pending > 0 ? 'var(--red)' : 'var(--text3)' }}>{fmt(pending)}</div>
                </div>
              </div>

              {/* Collection rate bar */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>Collection rate</span>
                  <span style={{ fontSize: 10, color: collection >= 80 ? 'var(--green)' : collection >= 50 ? 'var(--yellow)' : 'var(--red)', fontWeight: 600 }}>{collection}%</span>
                </div>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${collection}%`, height: '100%', background: collection >= 80 ? 'var(--green)' : collection >= 50 ? 'var(--yellow)' : 'var(--red)', borderRadius: 2, transition: 'width 0.4s ease' }} />
                </div>
              </div>

              {/* Status pills */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {active > 0 && <span style={{ fontSize: 11, background: 'var(--green-dim)', color: 'var(--green)', padding: '2px 8px', borderRadius: 10 }}>{active} active</span>}
                {completed > 0 && <span style={{ fontSize: 11, background: 'var(--blue-dim)', color: 'var(--blue)', padding: '2px 8px', borderRadius: 10 }}>{completed} done</span>}
                {disputed > 0 && <span style={{ fontSize: 11, background: 'var(--orange-dim)', color: 'var(--orange)', padding: '2px 8px', borderRadius: 10 }}>{disputed} disputed</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Project list for selected service */}
      {selected && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h2 style={{ fontSize: 16 }}>{selected} Projects</h2>
            <span style={{ fontSize: 13, color: 'var(--text3)' }}>{selectedProjects.length} total</span>
            <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', marginLeft: 'auto' }} onClick={() => setSelected(null)}>✕ Close</button>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Project</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Agreed</th>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {selectedProjects.map(p => {
                  const fin = p.financials?.[0];
                  const pending = getPending(fin);
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(p)}>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{p.project_code}</span></td>
                      <td className="td-main" style={{ maxWidth: 220 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.project_name}</div>
                      </td>
                      <td style={{ maxWidth: 160, fontSize: 13 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{custName(p.customer_id)}</div>
                      </td>
                      <td>{statusBadge(p.status)}</td>
                      <td style={{ color: 'var(--text)', fontWeight: 500 }}>{fmt(fin?.agreed_fee)}</td>
                      <td style={{ color: 'var(--green)' }}>{fmt(fin?.paid_amount)}</td>
                      <td style={{ color: pending > 0 ? 'var(--red)' : 'var(--text3)', fontWeight: pending > 0 ? 600 : 400 }}>{fmt(pending)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{fmtDate(p.order_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailModal && (
        <ProjectDetailModal
          project={detailModal}
          customers={customers}
          onClose={() => setDetailModal(null)}
          onRefresh={() => setDetailModal(null)}
          user={user}
        />
      )}
    </div>
  );
}
